import { hasFullConfiguratorAccess, getAccessibleVendors } from 'config/vendorRegistry';
import { deriveEffectiveConsultationMode } from 'utils/consultationView';
import { showCustomMetricsPanel } from 'utils/customMetricsEnabled';
import { PERM, type Perm } from './permissions';

export type Tier = 'locked' | 'vendorGroup' | 'full' | 'special';

export type AbilityInput = {
  groups: string[];
  vendor: { id: string; businessRules: { forceConsultationMode?: boolean } };
  consultationMode: boolean;
};

export type AbilityFlags = {
  hasSpecialAccess: boolean;
  hasFullConfigurator: boolean;
  hasExecAccess: boolean;
  hasAdminAccess: boolean;
  isCatalogAuthor: boolean;
  canToggleConsultation: boolean;
};

export type Ability = {
  can(p: Perm): boolean;
  tier: Tier;
  roleLabel: string;
  effectiveConsultationMode: boolean;
  flags: AbilityFlags;
};

const ROLE_LABEL: Record<Tier, string> = {
  locked: 'Consultation',
  vendorGroup: 'Consultation',
  full: 'Full Configurator',
  special: 'Special Pricing',
};

/**
 * The one place Cognito groups are interpreted for the UI (ui-architecture-
 * guidelines §4). Wraps — never re-derives — the existing rules:
 *   specialPricing ⟹ fullConfigurator    (vendorRegistry.hasFullConfiguratorAccess)
 *   effective = consultationMode || !canToggle   (utils/consultationView)
 *   vendor access is per-vendor group ONLY (vendorRegistry.getAccessibleVendors);
 *     tier groups grant no hardware, and a user with no vendor group never
 *     reaches this shell — ConfiguratorGate shows them the Home page instead.
 * UX only: server-side authorization is each model's @auth rules.
 */
export function defineAbility({ groups, vendor, consultationMode }: AbilityInput): Ability {
  const hasSpecialAccess = groups.includes('specialPricing');
  const hasFullConfigurator: boolean = hasFullConfiguratorAccess(groups);
  const hasExecAccess = groups.includes('execAccess');
  const hasDualCpuAccess = groups.includes('dualCpuAccess');
  const hasAdminAccess = groups.includes('adminAccess');
  const isCatalogAuthor = groups.includes('catalogAuthor');
  const canToggleConsultation = hasFullConfigurator;

  // Dell pins consultation on (App.js handleVendorChange / init effect); the
  // Toggle renders pinned right via forceRight.
  const forced = vendor.businessRules.forceConsultationMode === true;
  const effectiveConsultationMode: boolean =
    forced || deriveEffectiveConsultationMode(consultationMode, canToggleConsultation);

  const accessibleVendors: string[] = getAccessibleVendors(groups);
  // Derived from the registry, not a hand-listed pair: aicHardware is a real
  // group since 2026-09-22 and listing dell/smc here would have classed an
  // AIC-only user as locked.
  const hasVendorGroup = accessibleVendors.length > 0;

  const tier: Tier = hasSpecialAccess ? 'special'
    : hasFullConfigurator ? 'full'
    : hasVendorGroup ? 'vendorGroup'
    : 'locked';

  const pricingVisible = !effectiveConsultationMode;

  const grants: Record<Perm, boolean> = {
    [PERM.VENDOR_SELECT]: accessibleVendors.length > 1,
    [PERM.CONSULTATION_TOGGLE]: canToggleConsultation,
    [PERM.PRICING_VIEW]: pricingVisible,
    [PERM.RATES_EDIT]: pricingVisible && hasSpecialAccess,
    [PERM.QUARTER_ALL]: hasSpecialAccess,
    [PERM.ADMIN_SKUS]: hasSpecialAccess,
    [PERM.QUOTE_GENERATE]: pricingVisible && hasSpecialAccess,
    [PERM.SUMMARY_PDF]: pricingVisible,
    [PERM.EXEC_CUSTOM_PRICING]: pricingVisible && hasExecAccess,
    // CONF-212: wraps the classic gate (kill switch + execAccess + not in
    // consultation mode) rather than restating it.
    [PERM.EXEC_CUSTOM_METRICS]: showCustomMetricsPanel({ hasExecAccess, consultationMode: effectiveConsultationMode }),
    // CONF-210: a hardware choice, not a pricing view, so not gated on
    // pricingVisible — a holder in consultation mode can still pick the node.
    // CONF-259: its own group, so it can be given without exec pricing.
    [PERM.FLASH_DUAL_CPU]: hasDualCpuAccess,
    [PERM.LOAD_TOTAL_COST]: hasFullConfigurator,
    [PERM.ADMIN_VIEW]: hasAdminAccess || isCatalogAuthor,
    [PERM.ADMIN_MANAGE]: hasAdminAccess,
    [PERM.CATALOG_AUTHOR]: isCatalogAuthor,
  };

  return {
    can: (p) => grants[p] === true,
    tier,
    roleLabel: ROLE_LABEL[tier],
    effectiveConsultationMode,
    flags: { hasSpecialAccess, hasFullConfigurator, hasExecAccess, hasAdminAccess, isCatalogAuthor, canToggleConsultation },
  };
}
